import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Paper,
  Chip,
  Fade,
  Slide,
  Card,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Stop as StopIcon,
  Factory as FactoryIcon,
  Engineering as EngineeringIcon
} from "@mui/icons-material";
import axios from 'axios';
import './ProductionStart.css';
import ShiftRemarksButton from './shiftRemarksButton';
import ViewJobPlan from './ViewJobPlan';
import PropTypes from 'prop-types';
import useSessionLossRedirect from './useSessionLossRedirect';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ProductionStart = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const handleSessionLoss = useSessionLossRedirect();

  const { username, jobNumber, customer, operation, product, start_datetime, machine_code, startedTime } = props;
  const [popupOpen, setPopupOpen] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Shared snackbar state for inline error/status feedback. Sits above
  // the production Dialog (zIndex bumped) so the operator can read
  // messages even while the popup is open.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error", // "error" | "warning" | "info" | "success"
  });

  const showSnackbar = (message, severity = "error") => {
    setSnackbar({ open: true, message, severity });
  };
  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Check for ongoing operations on mount
  useEffect(() => {
    const fetchOngoingOperation = async () => {
      if (jobNumber && username && start_datetime && machine_code) {
        try {
          const response = await axios.get(
            `${API_BASE_URL}/Ongoingoperationlog`,
            {
              params: {
                employee_id: username,
                machine_code: machine_code,
                start_datetime
              },
            }
          );

          if (response.data.length > 0) {
            const ongoingOperation = response.data[0];
            if (ongoingOperation.operation_type === 'Production') {
              setStartTime(new Date(ongoingOperation.start_datetime));
              setPopupOpen(true);
            }
          }
        } catch (error) {
          console.error("Error fetching ongoing operation:", error);
          if (handleSessionLoss(error)) {
            showSnackbar("Session expired. Redirecting to login...", "error");
          }
          // Other errors stay silent here — operator didn't trigger this
          // fetch directly, so no need to alarm them on a transient blip.
        }
      }
    };

    fetchOngoingOperation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobNumber, username, start_datetime, machine_code]);

  const handleOpenpopup = async () => {
    setIsLoading(true);
    try {
      const machineCodeResponse = await axios.get(`${API_BASE_URL}/getMachineCode?username=${username}`);

      // Defensive check: backend can return 200 with empty body when an
      // admin clears a session, rather than a clean 401/403. Treat that
      // case as session loss too.
      if (!machineCodeResponse?.data?.machineCode) {
        showSnackbar("Session expired. Redirecting to login...", "error");
        // Reuse the hook by synthesizing a 401 — keeps redirect logic
        // in one place.
        handleSessionLoss({ response: { status: 401 } });
        return;
      }

      const payload = {
        employee_id: username,
        job_number: jobNumber,
        customer: customer,
        product: product,
        main_operation: operation,
        operation_description: 'Production',
        start_datetime: new Date().toISOString().slice(0, 19).replace('T', ' '),
        machine_code: machineCodeResponse.data.machineCode,
        active_status: 'Ongoing',
        operation_type: 'Production',
        JobStartedTime: startedTime
      };

      const response = await axios.post(`${API_BASE_URL}/startProduction`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        const { start_datetime } = response.data;
        setStartTime(new Date(start_datetime));
        setPopupOpen(true);
      }
    } catch (error) {
      console.error("Error sending operation log:", error);
      if (handleSessionLoss(error)) {
        showSnackbar("Session expired. Redirecting to login...", "error");
      } else {
        showSnackbar("Failed to start production. Please try again.", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosepopup = async () => {
    try {
      const endDateTime = new Date().toISOString().slice(0, 19).replace('T', ' ');

      const payload = {
        employee_id: username,
        start_datetime: startTime.toISOString().slice(0, 19).replace("T", " "),
        end_datetime: endDateTime,
        active_status: 'Finished'
      };

      const response = await axios.post(`${API_BASE_URL}/updateOperationLog`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        setPopupOpen(false);
      }
    } catch (error) {
      console.error("Error updating operation log:", error);
      if (handleSessionLoss(error)) {
        showSnackbar("Session expired. Redirecting to login...", "error");
      } else {
        showSnackbar("Failed to stop production. Please try again.", "error");
      }
    }
  };

  return (
    <div>
      <Button
        variant="contained"
        size="large"
        onClick={handleOpenpopup}
        disabled={isLoading}
        startIcon={isLoading ?
          <CircularProgress size={24} sx={{ color: 'white' }} /> :
          <EngineeringIcon sx={{ fontSize: 28 }} />
        }
        sx={{
          minWidth: 220,
          px: 4,
          borderRadius: '16px',
          fontWeight: 700,
          fontSize: '1.1rem',
          textTransform: 'none',
          background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
          color: 'white',
          boxShadow: '0 8px 25px rgba(76, 175, 80, 0.4)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover:not(:disabled)': {
            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            transform: 'translateY(-3px)',
            boxShadow: '0 12px 35px rgba(76, 175, 80, 0.5)',
          },
          '&:disabled': {
            background: 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
            boxShadow: '0 4px 15px rgba(158, 158, 158, 0.3)',
          }
        }}
      >
        {isLoading ? "Starting Production..." : "Production Start"}
      </Button>

      {/* Production Dialog */}
      <Dialog
        open={popupOpen}
        maxWidth="md"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: 'linear-gradient(145deg, rgba(76, 175, 80, 0.15) 0%, rgba(46, 125, 50, 0.25) 50%, rgba(30, 30, 50, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            boxShadow: '0 25px 45px rgba(76, 175, 80, 0.2), 0 15px 25px rgba(0, 0, 0, 0.3)',
            overflow: 'hidden',
            position: 'relative',
            minHeight: isMobile ? '500px' : '600px',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #4caf50, #2e7d32, #4caf50)',
              backgroundSize: '200% 100%',
              animation: 'gradient 3s ease infinite',
            }
          }
        }}
      >
        {isLoading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              borderRadius: '24px 24px 0 0',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #4caf50, #2e7d32)',
              }
            }}
          />
        )}

        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{
            p: 3,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.3) 0%, rgba(46, 125, 50, 0.3) 50%, rgba(40, 40, 70, 0.9) 100%)',
            borderBottom: '1px solid rgba(76, 175, 80, 0.3)',
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <ShiftRemarksButton Employee_ID={username} />
                <ViewJobPlan username={username} />
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                <FactoryIcon
                  sx={{
                    fontSize: 40,
                    color: '#4caf50',
                    filter: 'drop-shadow(0 4px 8px rgba(76, 175, 80, 0.4))'
                  }}
                />
                <Typography
                  variant="h4"
                  fontWeight="700"
                  sx={{
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                  }}
                >
                  Production Active
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={`Job: ${jobNumber}`}
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}
                />
                <Chip
                  label={`Customer: ${customer}`}
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}
                />
              </Box>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, position: 'relative', height: '400px' }}>
          {/* NOTE: Background animation removed as requested */}

          {/* Content */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            background: 'linear-gradient(transparent, rgba(76, 175, 80, 0.2) 30%, rgba(20, 20, 40, 0.95) 100%)',
            p: 4,
            textAlign: 'center',
            backdropFilter: 'blur(10px)',
          }}>
            <Typography
              variant="h3"
              fontWeight="700"
              sx={{
                color: 'white',
                textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
                mb: 2
              }}
            >
              {product}
            </Typography>

            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              flexWrap: 'wrap'
            }}>
              <Paper sx={{
                px: 3,
                py: 1.5,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.25) 0%, rgba(255, 255, 255, 0.15) 100%)',
                backdropFilter: 'blur(15px)',
                borderRadius: '12px',
                border: '1px solid rgba(76, 175, 80, 0.4)',
              }}>
                <Typography variant="h6" fontWeight="600" sx={{ color: 'white' }}>
                  Started: {startTime?.toLocaleTimeString()}
                </Typography>
              </Paper>
            </Box>

            <Fade in timeout={1000}>
              <Card
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(76, 175, 80, 0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>
                  <strong>Operation:</strong> {operation}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 1 }}>
                  Production is currently running. Monitor the process and stop when complete.
                </Typography>
              </Card>
            </Fade>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(20, 20, 40, 0.9) 100%)',
          borderTop: '1px solid rgba(76, 175, 80, 0.3)',
          justifyContent: 'center'
        }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleClosepopup}
            startIcon={<StopIcon />}
            sx={{
              minWidth: 220,
              borderRadius: '16px',
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.1rem',
              textTransform: 'none',
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              boxShadow: '0 8px 25px rgba(244, 67, 54, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 35px rgba(244, 67, 54, 0.5)',
              }
            }}
          >
            Stop Production
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inline error / status feedback. Sits above the production Dialog
          so the operator can read messages even while the popup is open. */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
};

ProductionStart.propTypes = {
  username: PropTypes.string.isRequired,
  jobNumber: PropTypes.string.isRequired,
  customer: PropTypes.string.isRequired,
  operation: PropTypes.string.isRequired,
  product: PropTypes.string.isRequired,
  start_datetime: PropTypes.string.isRequired,
  machine_code: PropTypes.string.isRequired,
  startedTime: PropTypes.string.isRequired,
};

export default ProductionStart;